import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";
import { BLOG_POSTS } from "@/data/blog";
import { tr } from "@/i18n";

const TONES = {
  light: {
    category: "text-[#211712]/70",
    title: "text-navy group-hover:text-gold",
    excerpt: "text-navy/60",
    cta: "text-navy border-navy/25 group-hover:border-gold group-hover:text-gold",
  },
  dark: {
    category: "text-gold",
    title: "text-[#F3EFE6] group-hover:text-gold-bright",
    excerpt: "text-[#F3EFE6]/60",
    cta: "text-gold border-gold/40 group-hover:border-gold-bright group-hover:text-gold-bright",
  },
};

function ArticleText({ post, large = false, cta, tone = "light" }) {
  const t = TONES[tone];
  return (
    <div>
      <p className={`text-[0.62rem] uppercase tracking-[0.3em] ${t.category}`}>{tr(post.category)}</p>
      <h3 className={`font-serif font-light leading-tight mt-4 transition-colors duration-300 ${t.title} ${large ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
        {tr(post.title)}
      </h3>
      <p className={`mt-4 text-sm font-light leading-relaxed line-clamp-4 ${t.excerpt}`}>{tr(post.excerpt)}</p>
      <span className={`inline-flex items-center gap-2.5 mt-6 text-[0.62rem] uppercase tracking-[0.28em] border-b pb-1.5 transition-colors duration-300 ${t.cta}`}>
        {tr(cta)}
        <ArrowRight strokeWidth={1.5} size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  );
}

function ArticleCard({ post, onOpen, cta, tone = "light", surface = "" }) {
  const framed = Boolean(surface);
  return (
    <article
      data-testid={`blog-card-${post.id}`}
      onClick={() => onOpen(post.id)}
      className={`group cursor-pointer ${surface}`}
    >
      <div className={`relative overflow-hidden aspect-[4/3] ${framed ? "" : "mb-6"}`}>
        <img
          src={post.image}
          alt={tr(post.title)}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
        />
      </div>
      <div className={framed ? "p-6 sm:p-8" : ""}>
        <ArticleText post={post} cta={cta} tone={tone} />
      </div>
    </article>
  );
}

export default function Blog() {
  const [openId, setOpenId] = useState(null);
  const openPost = BLOG_POSTS.find((p) => p.id === openId) || null;
  const readerBlocks = openPost ? openPost.blocks || (openPost.paragraphs || []).map((p) => ({ p })) : [];
  const firstReaderPara = readerBlocks.findIndex((b) => b.p);

  const [hero, side, ...rest] = BLOG_POSTS;
  const rowTwo = rest.slice(0, 3);

  useEffect(() => {
    document.body.style.overflow = openPost ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openPost]);

  return (
    <div data-testid="blog-page" className="bg-ivory text-navy">
      {/* ——— SECTION 1 · LEATHER BROWN — JOURNAL HEADER + HERO STORY ——— */}
      <section className="relative bg-[#211712] text-[#F3EFE6] overflow-hidden grain border-b border-[#C9A254]/20">
        <div className="relative max-w-4xl mx-auto px-6 pt-44 pb-16 lg:pt-52 lg:pb-20 text-center">
          <Reveal>
            <p className="text-[0.7rem] uppercase tracking-[0.35em] text-gold font-medium">{tr("The Delux Journal")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-7 text-[#F3EFE6]">
              {tr("A world of possibility,")} <em className="gold-shimmer italic">{tr("thoughtfully arranged.")}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#F3EFE6]/60 font-light leading-relaxed max-w-xl mx-auto text-sm">
              {tr("Perspectives on exceptional travel, considered living and experiences worth making time for.")}
            </p>
          </Reveal>
        </div>

        <div className="relative max-w-[110rem] mx-auto px-6 lg:px-16 pb-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-14 items-center">
            <Reveal className="lg:col-span-6">
              <div
                data-testid="blog-hero-image"
                onClick={() => setOpenId(hero.id)}
                className="group relative overflow-hidden aspect-[4/3] lg:aspect-[16/11] cursor-pointer border border-white/10"
              >
                <img
                  src={hero.image}
                  alt={tr(hero.title)}
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-3">
              <article data-testid="blog-featured" onClick={() => setOpenId(hero.id)} className="group cursor-pointer">
                <ArticleText post={hero} large cta="Discover the story" tone="dark" />
              </article>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-3">
              <ArticleCard
                post={side}
                onOpen={setOpenId}
                cta="Read more"
                tone="dark"
                surface="bg-[#18100C]/60 border border-white/10 backdrop-blur-sm transition-colors duration-500 hover:border-gold/40"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ——— SECTION 2 · IVORY — EDITORIAL ESSENTIALS ——— */}
      <section className="relative bg-ivory text-navy py-20 lg:py-28">
        <div className="max-w-[110rem] mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {rowTwo.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <ArticleCard
                  post={p}
                  onOpen={setOpenId}
                  cta="Read more"
                  tone="light"
                  surface="bg-white/80 border border-[#211712]/10 transition-all duration-300 hover:shadow-xl"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— SECTION 4 · LEATHER BROWN — CLOSING BAND ——— */}
      <section data-testid="blog-cta-band" className="relative bg-[#211712] text-[#F3EFE6] py-20 lg:py-24 border-t border-[#C9A254]/30 grain overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="text-[0.62rem] uppercase tracking-[0.35em] text-gold">{tr("The Delux Journal")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-3xl sm:text-4xl leading-tight mt-6">
              {tr("Every extraordinary journey begins with a conversation.")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="blog-cta-button"
              className="inline-block mt-10 bg-gold text-black text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-gold-bright transition-colors duration-300"
            >
              {tr("Speak with an advisor")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ——— READER OVERLAY (immersive dark) ——— */}
      <AnimatePresence>
        {openPost && (
          <motion.div
            data-testid="blog-reader"
            data-lenis-prevent
            className="fixed inset-0 z-[70] bg-[#060607]/95 overflow-y-auto overscroll-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="max-w-3xl mx-auto px-6 pt-24 pb-28 relative">
              <button
                data-testid="blog-reader-close"
                aria-label={tr("Close article")}
                onClick={() => setOpenId(null)}
                className="fixed top-6 right-6 z-10 w-12 h-12 rounded-full border border-white/20 bg-black/60 backdrop-blur flex items-center justify-center text-white/70 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <X strokeWidth={1.25} size={22} />
              </button>
              <motion.div
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              >
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-gold">{tr(openPost.category)}</p>
                <h1 data-testid="blog-reader-title" className="font-serif font-light text-4xl sm:text-5xl leading-[1.08] mt-5 text-white">
                  {tr(openPost.title)}
                </h1>
                <p className="mt-5 text-[0.62rem] uppercase tracking-[0.24em] text-white/35">
                  {tr(openPost.date)} · {tr(openPost.readTime)}
                </p>
                <div className="mt-10 overflow-hidden aspect-[16/9]">
                  <img src={openPost.image} alt={tr(openPost.title)} className="w-full h-full object-cover" />
                </div>
                <div className="mt-12">
                  {readerBlocks.map((b, i) => (
                    <div key={i}>
                      {b.h && (
                        <h3 data-testid={`blog-reader-heading-${i}`} className="font-serif font-light text-2xl sm:text-[1.7rem] text-gold mt-12 mb-5">
                          {tr(b.h)}
                        </h3>
                      )}
                      {b.p && (
                        <p className={`font-light leading-[1.9] text-white/75 mb-8 ${i === firstReaderPara ? "text-lg first-letter:font-serif first-letter:text-6xl first-letter:text-gold first-letter:mr-3 first-letter:float-start first-letter:leading-[0.85]" : "text-base"}`}>
                          {tr(b.p)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-14 pt-8 border-t border-white/10 text-[0.62rem] uppercase tracking-[0.3em] text-white/35">
                  {tr("The Delux Journal — written by our advisors")}
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
